// Dua summary voyage -> baris perbandingan. Import cuma model summary biar tetap bisa dites node.
import {
  buildKpis,
  buildMainEngine,
  buildAuxEngine,
  buildWeather,
  buildOperationMode,
  engineLabel,
} from './voyageSummaryModel.js';

const METERS_PER_NM = 1852;
const KMH_PER_KNOT = 1.852;

const positive = (value) => (typeof value === 'number' && value > 0 ? value : null);
const divide = (top, bottom) => (positive(top) !== null && positive(bottom) !== null ? top / bottom : null);
const toHours = (seconds) => (positive(seconds) === null ? null : seconds / 3600);
// floor: 0.9998 jangan jadi 100%
const floorPercent = (ratio) => Math.floor((ratio || 0) * 1000) / 10;
const unionKeys = (left, right) => [...new Set([...Object.keys(left), ...Object.keys(right)])];

// rows = metrik tunggal; keyedRows = satu baris per engine/AE/arah, urut per metrik
const GROUPS = [
  {
    key: 'overview',
    title: 'Overview',
    keepEmpty: true,
    rows: [
      { key: 'voyageNo', label: 'Voyage No.', kind: 'text' },
      { key: 'asset', label: 'Asset', kind: 'text' },
      { key: 'period', label: 'Period', kind: 'text' },
      { key: 'route', label: 'Route', kind: 'text' },
      { key: 'cargo', label: 'Cargo', kind: 'text' },
      { key: 'duration', label: 'Duration', kind: 'duration' },
      {
        key: 'distance', label: 'Distance', kind: 'number', unit: 'NM', coverage: ['gps'],
      },
      {
        key: 'averageSpeed', label: 'Avg Speed', kind: 'number', unit: 'kn', coverage: ['gps'],
      },
    ],
  },
  {
    key: 'efficiency',
    title: 'Fuel & Efficiency',
    rows: [
      {
        key: 'meCons', label: 'ME Cons', kind: 'number', unit: 'L', better: 'lower', coverage: ['flowmeter'],
      },
      {
        key: 'aeCons', label: 'AE Cons (By Constanta)', kind: 'number', unit: 'L', better: 'lower', coverage: ['ae'],
      },
      {
        key: 'fuelPerNm', label: 'ME Fuel / NM', kind: 'number', unit: 'L/NM', better: 'lower', coverage: ['flowmeter', 'gps'],
      },
      {
        key: 'fuelPerVoyageHour', label: 'ME Fuel / Voyage Hour', kind: 'number', unit: 'L/h', better: 'lower', coverage: ['flowmeter'],
      },
    ],
    keyedRows: [
      {
        collection: 'engines', field: 'fuelPerRunningHour', label: 'Fuel / Running Hour', kind: 'number', unit: 'L/h', better: 'lower', coverage: ['flowmeter'],
      },
    ],
  },
  {
    key: 'mainEngine',
    title: 'Main Engine',
    rows: [],
    keyedRows: [
      {
        collection: 'engines', field: 'averageRpm', label: 'Avg RPM', kind: 'number', unit: 'RPM', coverage: ['rpm'],
      },
      {
        collection: 'engines', field: 'engineHours', label: 'Engine Hours', kind: 'duration', coverage: ['rpm'],
      },
      {
        collection: 'engines', field: 'fuel', label: 'Fuel', kind: 'number', unit: 'L', coverage: ['flowmeter'],
      },
    ],
  },
  {
    key: 'auxEngine',
    title: 'Auxiliary Engine',
    rows: [],
    keyedRows: [
      {
        collection: 'aes', field: 'running', label: 'Running', kind: 'duration', coverage: ['ae'],
      },
      {
        collection: 'aes', field: 'fuel', label: 'Fuel', kind: 'number', unit: 'L', coverage: ['ae'],
      },
    ],
  },
  {
    key: 'weather',
    title: 'Weather & Wave',
    rows: [
      {
        key: 'waveHeight', label: 'Avg Wave Height', kind: 'number', unit: 'm', coverage: ['gps'],
      },
      {
        key: 'wavePeriod', label: 'Avg Wave Period', kind: 'number', unit: 's', coverage: ['gps'],
      },
      { key: 'seaState', label: 'Sea State', kind: 'text' },
    ],
    keyedRows: [
      {
        collection: 'directions', field: 'share', label: 'Share', kind: 'number', unit: '%', decimals: 1, coverage: ['gps'],
      },
    ],
  },
];

export function deltaOf(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
  return ((b - a) / a) * 100;
}

export function toneOf(deltaPct, better) {
  if (better !== 'lower' || deltaPct === null || deltaPct === 0) return 'neutral';
  return deltaPct < 0 ? 'better' : 'worse';
}

export function coverageWarning(coverageA, coverageB, types) {
  const isIncomplete = (item) => !!item && item.complete === false;
  const sideText = (item) => (item ? `${floorPercent(item.ratio)}%` : 'N/A');
  const parts = types.flatMap((type) => {
    const itemA = coverageA?.[type];
    const itemB = coverageB?.[type];
    if (!isIncomplete(itemA) && !isIncomplete(itemB)) return [];
    return [`${type.toUpperCase()} coverage · A ${sideText(itemA)} · B ${sideText(itemB)}`];
  });
  return parts.length ? parts.join('; ') : null;
}

function cargoText(master) {
  if (!master) return null;
  if (!master.mvygCargoStatus) return 'No Cargo';
  return ['With Cargo', `${master.mvygCargoLoadKg || 0} kg`, master.mvygCargoType].filter(Boolean).join(' · ');
}

function routeText(master) {
  const origin = master?.mvygOriginLndm?.lndmName;
  const destination = master?.mvygDestinationLndm?.lndmName;
  if (!origin && !destination) return null;
  return `${origin || '-'} → ${destination || '-'}`;
}

function sideValues({ summary, durationSeconds, periodText }) {
  const master = summary?.master;
  const kpis = buildKpis(summary);
  const weather = buildWeather(summary);
  const meCons = positive(kpis.meConsLiters);
  const distance = positive(kpis.distanceMeters) === null ? null : kpis.distanceMeters / METERS_PER_NM;

  const engines = {};
  buildMainEngine(summary).engines.forEach((engine) => {
    engines[engine.key] = {
      include: positive(engine.rpm) !== null || positive(engine.runningSeconds) !== null,
      averageRpm: positive(engine.rpm),
      engineHours: positive(engine.runningSeconds),
      fuel: positive(engine.fuelLiters),
      fuelPerRunningHour: divide(engine.fuelLiters, toHours(engine.runningSeconds)),
    };
  });

  const aes = {};
  buildAuxEngine(summary).engines.forEach((engine) => {
    aes[engine.key] = {
      include: positive(engine.runningSeconds) !== null || positive(engine.fuelLiters) !== null,
      label: engine.key,
      running: positive(engine.runningSeconds),
      fuel: positive(engine.fuelLiters),
    };
  });

  const directions = {};
  weather.directions.forEach((direction) => {
    directions[direction.key] = { include: true, label: direction.label, share: direction.share };
  });

  return {
    massId: master?.mvygMassId ?? null,
    voyageNo: master?.mvygNumber || null,
    asset: master?.mvygMass?.massName || null,
    period: periodText || null,
    route: routeText(master),
    cargo: cargoText(master),
    duration: positive(durationSeconds),
    distance,
    averageSpeed: positive(kpis.averageSpeedKmh) === null ? null : kpis.averageSpeedKmh / KMH_PER_KNOT,
    meCons,
    aeCons: positive(kpis.aeConsLiters),
    fuelPerNm: divide(meCons, distance),
    fuelPerVoyageHour: divide(meCons, toHours(durationSeconds)),
    waveHeight: positive(weather.waveHeight),
    wavePeriod: positive(weather.wavePeriod),
    seaState: weather.seaState,
    engines,
    aes,
    directions,
  };
}

function makeRow(def, key, label, valueA, valueB, coverage) {
  // baris porsi (%) dibandingin pakai selisih poin, persen dari persen nyesatin
  const isShare = def.unit === '%';
  const deltaPct = def.kind === 'text' || isShare ? null : deltaOf(valueA, valueB);
  const deltaPoints = isShare && Number.isFinite(valueA) && Number.isFinite(valueB) ? valueB - valueA : null;
  return {
    key,
    label,
    kind: def.kind,
    unit: def.unit || '',
    decimals: def.decimals ?? 2,
    a: valueA ?? null,
    b: valueB ?? null,
    deltaPct,
    deltaPoints,
    tone: toneOf(deltaPct, def.better),
    warn: def.coverage ? coverageWarning(coverage.a, coverage.b, def.coverage) : null,
  };
}

export function buildComparison({ a, b }) {
  const valuesA = sideValues(a);
  const valuesB = sideValues(b);
  const coverage = { a: a.summary?.coverage, b: b.summary?.coverage };

  const keysOf = (collection) => unionKeys(valuesA[collection], valuesB[collection])
    .filter((key) => valuesA[collection][key]?.include || valuesB[collection][key]?.include);
  const keys = {
    engines: keysOf('engines'),
    aes: keysOf('aes'),
    directions: keysOf('directions'),
  };
  const labelOf = (collection, key) => {
    if (collection === 'engines') return engineLabel(key, keys.engines.length);
    return valuesA[collection][key]?.label || valuesB[collection][key]?.label || key;
  };

  const groups = GROUPS.map((group) => {
    const rows = group.rows.map((def) => makeRow(def, def.key, def.label, valuesA[def.key], valuesB[def.key], coverage));
    (group.keyedRows || []).forEach((def) => {
      keys[def.collection].forEach((itemKey) => {
        const valueA = valuesA[def.collection][itemKey]?.[def.field] ?? null;
        const valueB = valuesB[def.collection][itemKey]?.[def.field] ?? null;
        if (valueA === null && valueB === null) return;
        const label = `${labelOf(def.collection, itemKey)} ${def.label}`;
        rows.push(makeRow(def, `${def.field}:${itemKey}`, label, valueA, valueB, coverage));
      });
    });
    return { key: group.key, title: group.title, rows };
  }).filter((group, index) => GROUPS[index].keepEmpty || group.rows.some((row) => row.a !== null || row.b !== null));

  return {
    differentVessels: String(valuesA.massId) !== String(valuesB.massId),
    groups,
  };
}

// operation mode per engine: bar 100% A di atas B, warna mode dikunci ke urutan modeKeys
export function buildOperationModeComparison(summaryA, summaryB) {
  const modeA = buildOperationMode(summaryA);
  const modeB = buildOperationMode(summaryB);
  const modeKeys = [...new Set([...modeA.modeKeys, ...modeB.modeKeys])];
  const engineKeys = [...new Set([...modeA.engines, ...modeB.engines].map((engine) => engine.key))];

  const sideOf = (model, key) => {
    const engine = model.engines.find((item) => item.key === key);
    if (!engine || !engine.modes.some((mode) => mode.seconds > 0)) return null;
    return modeKeys
      .map((modeKey) => engine.modes.find((mode) => mode.key === modeKey))
      .filter((mode) => mode && mode.seconds > 0);
  };

  const engines = engineKeys.map((key) => ({
    key,
    label: engineLabel(key, engineKeys.length),
    a: sideOf(modeA, key),
    b: sideOf(modeB, key),
  })).filter((engine) => engine.a || engine.b);

  return { modeKeys, engines, hasData: engines.length > 0 };
}
